import { useState, useEffect, useCallback } from 'react';
import { Check, X, ExternalLink, RefreshCw, FileText, Table2, Loader2, Save, Eye, UserPlus, Trash2, Shield } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const SettingsView = ({ user }) => {
  const [adsStatus, setAdsStatus] = useState(null);
  const [semrushStatus, setSemrushStatus] = useState(null);
  const [kwpuStatus, setKwpuStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templateSettings, setTemplateSettings] = useState(null);
  const [templateEdits, setTemplateEdits] = useState({});
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [approvedEmails, setApprovedEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);

  
  

  const fetchStatus = useCallback(async () => {
    try {
      const [adsRes, srRes, kwRes] = await Promise.all([
        authFetch(`${API}/api/google-ads/auth/status`),
        authFetch(`${API}/api/semrush/status`),
        authFetch(`${API}/api/kwpu/status`),
      ]);
      if (adsRes.ok) setAdsStatus(await adsRes.json());
      if (srRes.ok) setSemrushStatus(await srRes.json());
      if (kwRes.ok) setKwpuStatus(await kwRes.json());
    } catch (e) { console.error('Failed to load integration status:', e); }
    finally { setLoading(false); }
  }, []);

  const fetchTemplateSettings = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/settings/templates`);
      if (res.ok) {
        const data = await res.json();
        setTemplateSettings(data);
        const edits = {};
        for (const [key, val] of Object.entries(data.settings || {})) {
          edits[key] = typeof val === 'object' ? val.file_id || '' : val || '';
        }
        setTemplateEdits(edits);
      }
    } catch (e) { console.error('Failed to load template settings:', e); }
  }, []);

  const fetchApprovedEmails = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/auth/approved-emails`);
      if (res.ok) {
        const data = await res.json();
        setApprovedEmails(data.approved_emails || []);
      }
    } catch (e) { console.error('Failed to load approved emails:', e); }
  }, []);

  useEffect(() => { fetchStatus(); fetchTemplateSettings(); fetchApprovedEmails(); }, [fetchStatus, fetchTemplateSettings, fetchApprovedEmails]);

  const addApprovedEmail = async () => {
    if (!newEmail) return;
    setAddingEmail(true);
    try {
      const res = await authFetch(`${API}/api/auth/approved-emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail, name: newName }),
      });
      if (res.ok) {
        toast.success(`${newEmail} added to approved list`);
        setNewEmail('');
        setNewName('');
        fetchApprovedEmails();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add email');
      }
    } catch (e) { toast.error('Failed to add email'); }
    finally { setAddingEmail(false); }
  };

  const removeApprovedEmail = async (email) => {
    try {
      const res = await authFetch(`${API}/api/auth/approved-emails/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success(`${email} removed`);
        fetchApprovedEmails();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to remove');
      }
    } catch (e) { toast.error('Failed to remove email'); }
  };

  const saveTemplateSettings = async () => {
    setSavingTemplates(true);
    try {
      const res = await authFetch(`${API}/api/settings/templates`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateEdits),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.warnings?.length) {
          toast.warning(`Saved with warnings: ${data.warnings.join('; ')}`);
        } else {
          toast.success('Template settings saved');
        }
        fetchTemplateSettings();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save settings');
      }
    } catch (e) { toast.error('Failed to save'); }
    finally { setSavingTemplates(false); }
  };

  const previewTemplate = async (key) => {
    if (!templateEdits[key]) { toast.error('No file ID configured for this template'); return; }
    setPreviewKey(key);
    setLoadingPreview(true);
    setPreviewData(null);
    try {
      const res = await authFetch(`${API}/api/settings/templates/preview/${key}`);
      if (res.ok) setPreviewData(await res.json());
      else toast.error('Preview failed — check file ID');
    } catch (e) { toast.error('Preview failed'); }
    finally { setLoadingPreview(false); }
  };

  const handleConnectGoogleAds = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/google-ads/auth/start`);
      if (res.ok) {
        const data = await res.json();
        window.open(data.authorization_url, '_blank', 'width=600,height=700');
        toast.info('Complete authorization in the popup window, then click "Refresh Status" below.');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to start OAuth');
      }
    } catch (e) { toast.error('Network error'); }
  };

  const StatusBadge = ({ ok, label }) => (
    <div className="flex items-center gap-2">
      {ok ? (
        <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center"><Check size={12} className="text-emerald-600" /></span>
      ) : (
        <span className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center"><X size={12} className="text-red-500" /></span>
      )}
      <span className={`text-sm ${ok ? 'text-text-primary' : 'text-text-tertiary'}`}>{label}</span>
    </div>
  );

  return (
    <div className="space-y-8 animate-fade-up" data-testid="settings-view">
      <div>
        <h2 className="text-3xl font-semibold tracking-tight text-text-primary mb-1">Settings</h2>
        <p className="text-text-secondary">Manage integrations and API connections</p>
      </div>

      {/* Approved Access List */}
      {user.role === 'Admin' && (
        <Card className="glass" data-testid="approved-emails-section">
          <CardHeader>
            <CardTitle className="text-brand text-lg flex items-center gap-2">
              <Shield size={20} />
              Approved Access List
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-text-secondary">
              Only BCM employees on this list can sign in with Google SSO. New users get Client Service + Media Buyer access by default.
            </p>

            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs text-text-secondary">Email</Label>
                <Input
                  type="email"
                  placeholder="name@beebyclarkmeyler.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  data-testid="add-email-input"
                  className="mt-1"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-text-secondary">Name (optional)</Label>
                <Input
                  type="text"
                  placeholder="Full Name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  data-testid="add-name-input"
                  className="mt-1"
                />
              </div>
              <Button
                onClick={addApprovedEmail}
                disabled={addingEmail || !newEmail}
                className="bg-brand hover:bg-brand-hover text-white"
                data-testid="add-email-button"
              >
                <UserPlus size={16} className="mr-1" />
                {addingEmail ? 'Adding...' : 'Add'}
              </Button>
            </div>

            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {approvedEmails.length === 0 ? (
                <p className="p-3 text-sm text-text-tertiary text-center">No approved emails yet</p>
              ) : (
                approvedEmails.map((item) => (
                  <div key={item.email} className="flex items-center justify-between px-3 py-2 hover:bg-surface-page/50">
                    <div>
                      <span className="text-sm font-medium text-text-primary">{item.email}</span>
                      {item.name && <span className="text-xs text-text-secondary ml-2">({item.name})</span>}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeApprovedEmail(item.email)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                      data-testid={`remove-email-${item.email}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Google Ads Keyword Planner */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="text-brand text-lg">Google Ads — Keyword Planner</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-text-secondary">
            Connect your Google Ads account to get real keyword research data (search volume, competition, bid estimates) when generating campaign briefs.
          </p>

          {loading ? (
            <div className="animate-pulse h-20 bg-surface-page rounded-lg" />
          ) : adsStatus ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatusBadge ok={adsStatus.client_id} label="Client ID" />
                <StatusBadge ok={adsStatus.developer_token} label="Developer Token" />
                <StatusBadge ok={!!adsStatus.manager_customer_id} label="Manager Account" />
                <StatusBadge ok={adsStatus.has_refresh_token} label="OAuth Token" />
              </div>

              {adsStatus.configured ? (
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <p className="text-emerald-800 font-medium flex items-center gap-2">
                    <Check size={18} /> Keyword Planner is active — using live Google Ads data
                  </p>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <p className="text-amber-800 font-medium mb-2">OAuth authorization required</p>
                  <p className="text-amber-700 text-sm mb-3">
                    Click below to connect your Google Ads account. You'll be redirected to Google to authorize access, then return here.
                  </p>
                  <p className="text-amber-600 text-xs mb-4">
                    Make sure <code className="bg-amber-100 px-1 py-0.5 rounded">{process.env.REACT_APP_BACKEND_URL}/api/google-ads/auth/callback</code> is added as an authorized redirect URI in your Google Cloud Console.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handleConnectGoogleAds}
                  className="bg-brand hover:bg-brand-hover text-white gap-2"
                  data-testid="connect-google-ads-btn"
                >
                  <ExternalLink size={16} />
                  {adsStatus.configured ? 'Re-authorize Google Ads' : 'Connect Google Ads'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setLoading(true); fetchStatus(); }}
                  className="gap-2"
                  data-testid="refresh-status-btn"
                >
                  <RefreshCw size={16} /> Refresh Status
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-red-500 text-sm">Failed to load status</p>
          )}
        </CardContent>
      </Card>

      {/* SEMRush Keyword Research */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="text-brand text-lg">SEMRush — Keyword Research</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-text-secondary">
            SEMRush provides real search volume, CPC, competition, and keyword difficulty data for AI campaign generation.
          </p>
          {loading ? (
            <div className="animate-pulse h-12 bg-surface-page rounded-lg" />
          ) : semrushStatus?.configured ? (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="semrush-status-active">
              <p className="text-emerald-800 font-medium flex items-center gap-2">
                <Check size={18} /> SEMRush API is active — real keyword data powers campaign generation
              </p>
            </div>
          ) : (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="semrush-status-inactive">
              <p className="text-amber-800 font-medium">SEMRush API key not configured</p>
              <p className="text-amber-700 text-sm mt-1">Contact your administrator to set up the SEMRUSH_API_KEY in the backend environment.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KWPU — KeywordsPeopleUse */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="text-brand text-lg">KWPU — KeywordsPeopleUse</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-text-secondary">
            KWPU provides Google Autocomplete suggestions, semantic keywords, and Reddit/Quora forum questions for intent-based keyword clustering.
          </p>
          {loading ? (
            <div className="animate-pulse h-12 bg-surface-page rounded-lg" />
          ) : kwpuStatus?.configured ? (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="kwpu-status-active">
              <p className="text-emerald-800 font-medium flex items-center gap-2">
                <Check size={18} /> KWPU API is active — {kwpuStatus.tools?.join(', ')}
              </p>
            </div>
          ) : (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="kwpu-status-inactive">
              <p className="text-amber-800 font-medium">KWPU API key not configured</p>
              <p className="text-amber-700 text-sm mt-1">Contact your administrator to set up the KWPU_API_KEY in the backend environment.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Document Templates */}
      {user.role === 'Admin' && templateSettings && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-bold text-text-primary flex items-center gap-2" data-testid="template-settings-title">
              <FileText size={20} className="text-brand" /> Document Templates (Google Drive)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm text-text-secondary">
              Link Google Docs for SOW/Legal templates and a Google Sheet for the rate card. The system reads the latest version from Drive each time — change the template content in Drive, keep the same file ID.
            </p>
            {!templateSettings.drive_available && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                Google Drive service account not configured — template features will use built-in defaults.
              </div>
            )}

            {/* SOW Templates */}
            <div>
              <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-3">SOW Templates (Google Docs)</h3>
              <div className="space-y-3">
                {[
                  { key: 'sow_standard', label: 'Standard Project SOW' },
                  { key: 'sow_retainer', label: 'Monthly Retainer SOW' },
                  { key: 'sow_project_based', label: 'Project-Based SOW' },
                ].map(t => (
                  <TemplateRow
                    key={t.key} label={t.label} tkey={t.key} icon={<FileText size={14} className="text-brand" />}
                    value={templateEdits[t.key] || ''} info={templateSettings.settings?.[t.key]}
                    onChange={v => setTemplateEdits(e => ({ ...e, [t.key]: v }))}
                    onPreview={() => previewTemplate(t.key)}
                  />
                ))}
              </div>
            </div>

            {/* Rate Sheet */}
            <div>
              <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-3">Estimator Rate Sheet (Google Sheet)</h3>
              <TemplateRow
                label="Client Rate Sheet" tkey="rate_sheet" icon={<Table2 size={14} className="text-brand" />}
                value={templateEdits.rate_sheet || ''} info={templateSettings.settings?.rate_sheet}
                onChange={v => setTemplateEdits(e => ({ ...e, rate_sheet: v }))}
                onPreview={() => previewTemplate('rate_sheet')}
              />
              <div className="mt-2 ml-1">
                <Label className="text-xs text-text-tertiary">Sheet Range (e.g. Sheet1, Rates!A:B)</Label>
                <Input
                  value={templateEdits.rate_sheet_range || ''}
                  onChange={e => setTemplateEdits(ed => ({ ...ed, rate_sheet_range: e.target.value }))}
                  placeholder="Sheet1"
                  className="mt-1 max-w-xs text-sm"
                  data-testid="rate-sheet-range-input"
                />
              </div>
            </div>

            {/* Legal Templates */}
            <div>
              <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-3">Legal Templates (Google Docs)</h3>
              <div className="space-y-3">
                {[
                  { key: 'legal_nda', label: 'NDA Template' },
                  { key: 'legal_msa', label: 'MSA Template' },
                  { key: 'legal_io', label: 'IO Template' },
                  { key: 'legal_po', label: 'PO Template' },
                ].map(t => (
                  <TemplateRow
                    key={t.key} label={t.label} tkey={t.key} icon={<FileText size={14} className="text-brand" />}
                    value={templateEdits[t.key] || ''} info={templateSettings.settings?.[t.key]}
                    onChange={v => setTemplateEdits(e => ({ ...e, [t.key]: v }))}
                    onPreview={() => previewTemplate(t.key)}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={saveTemplateSettings} disabled={savingTemplates} className="bg-brand hover:bg-brand-hover" data-testid="save-template-settings">
                {savingTemplates ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Saving...</> : <><Save size={14} className="mr-1.5" /> Save Template Settings</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template Preview Modal */}
      {previewKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl m-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-text-primary">Template Preview: {previewKey}</h2>
              <button onClick={() => { setPreviewKey(null); setPreviewData(null); }} className="p-1 rounded-lg hover:bg-surface-page"><X size={20} /></button>
            </div>
            <div className="p-5">
              {loadingPreview ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-brand" size={28} /></div>
              ) : previewData?.type === 'sheet' ? (
                <div>
                  <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider text-text-tertiary">Rate Sheet ({Object.keys(previewData.rates || {}).length} roles)</h3>
                  <table className="w-full text-sm border-collapse">
                    <thead><tr className="bg-brand text-white"><th className="p-2 text-left">Role</th><th className="p-2 text-right">Rate</th></tr></thead>
                    <tbody>{Object.entries(previewData.rates || {}).map(([role, rate], i) => (
                      <tr key={role} className={i % 2 === 0 ? 'bg-white' : 'bg-surface-page'}><td className="p-2">{role}</td><td className="p-2 text-right font-medium">${rate.toLocaleString()}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : previewData?.type === 'doc' ? (
                <div>
                  <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider text-text-tertiary">Document Preview</h3>
                  <div className="border rounded-lg p-4 bg-white text-sm max-h-[60vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewData.html || '<p>No content</p>') }} />
                </div>
              ) : (
                <p className="text-text-tertiary text-center py-8">No preview data available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Reusable row for template file ID input
const TemplateRow = ({ label, tkey, icon, value, info, onChange, onPreview }) => {
  const fileInfo = info?.file_info;
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl border border-border-subtle bg-surface-page/50" data-testid={`template-row-${tkey}`}>
      <div className="mt-2">{icon}</div>
      <div className="flex-1 min-w-0">
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex gap-2 mt-1.5">
          <Input
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="Google Drive File ID"
            className="text-sm font-mono flex-1"
            data-testid={`template-input-${tkey}`}
          />
          {value && (
            <Button type="button" variant="outline" size="sm" onClick={onPreview} title="Preview" data-testid={`template-preview-${tkey}`}>
              <Eye size={14} />
            </Button>
          )}
        </div>
        {fileInfo && (
          <div className="flex items-center gap-2 mt-1.5 text-xs text-text-tertiary">
            <Check size={12} className="text-emerald-500" />
            <span className="font-medium text-emerald-700">{fileInfo.name}</span>
            <span>&middot; Modified {fileInfo.modified_time?.slice(0, 10)}</span>
            {fileInfo.web_view_link && <a href={fileInfo.web_view_link} target="_blank" rel="noreferrer" className="text-brand hover:underline flex items-center gap-0.5"><ExternalLink size={10} /> Open</a>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsView;
